import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  Badge,
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  Popover,
  Tooltip,
  Typography,
  CircularProgress,
  Avatar,
  Chip,
  useTheme,
  alpha,
  Button
} from '@mui/material';
import {
  Message,
  ChatBubble,
  Person
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ComplaintsService, { ComplaintThread } from 'src/services/complaints-service';

interface ComplaintsNotificationsProps {
  maxItems?: number;
}

const NotificationsBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    fontWeight: 600,
    fontSize: '0.75rem',
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    border: `2px solid ${theme.palette.background.paper}`,
    boxShadow: theme.shadows[2],
    animation: 'pulse 2s ease-in-out infinite'
  },
  '@keyframes pulse': {
    '0%': {
      boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0.7)}`
    },
    '70%': {
      boxShadow: `0 0 0 6px ${alpha(theme.palette.error.main, 0)}`
    },
    '100%': {
      boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0)}`
    }
  }
}));

const ComplaintItem = styled(ListItem)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.2s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    borderColor: alpha(theme.palette.primary.main, 0.2),
    transform: 'translateY(-1px)',
    boxShadow: theme.shadows[4]
  }
}));

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return date.toLocaleDateString();
};

export default function ComplaintsNotifications({
  maxItems = 10
}: ComplaintsNotificationsProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const theme = useTheme();
  const navigate = useNavigate();
  const [isOpen, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [threads, setThreads] = useState<ComplaintThread[]>([]);
  const complaintsService = ComplaintsService.getInstance();

  const loadThreads = useCallback(async () => {
    setLoading(true);
    try {
      const data = await complaintsService.getAllThreads();
      // Filter only threads with unread messages and sort by last activity
      const unreadThreads = data
        .filter(thread => thread.unreadByAdminCount > 0)
        .sort((a, b) => new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime())
        .slice(0, maxItems);
      setThreads(unreadThreads);
    } catch (error) {
      console.error('Failed to fetch complaint threads:', error);
      setThreads([]);
    }
    setLoading(false);
  }, [complaintsService, maxItems]);

  useEffect(() => {
    // Load initial count for badge
    const loadCount = async () => {
      try {
        await loadThreads();
      } catch (error) {
        console.error('Failed to load complaint count:', error);
      }
    };
    loadCount();

    // Poll for new complaints every 30 seconds
    const interval = setInterval(loadCount, 30000);
    return () => clearInterval(interval);
  }, [loadThreads]);

  useEffect(() => {
    if (isOpen) {
      loadThreads();
    }
  }, [isOpen, loadThreads]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleThreadClick = (threadId: string) => {
    navigate(`/complaints/${threadId}`);
    setOpen(false);
  };

  const handleViewAll = () => {
    navigate('/complaints');
    setOpen(false);
  };

  const unreadCount = threads.reduce((total, thread) => total + thread.unreadByAdminCount, 0);

  return (
    <>
      <Tooltip title="Complaints & Messages" arrow>
        <IconButton
          ref={ref}
          onClick={handleOpen}
          sx={{
            color: '#4CAF50',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: alpha('#4CAF50', 0.15),
              transform: 'scale(1.1) rotate(-5deg)',
              boxShadow: `0 4px 12px ${alpha('#4CAF50', 0.3)}`
            }
          }}
        >
          <NotificationsBadge
            badgeContent={unreadCount}
            max={99}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Message />
          </NotificationsBadge>
        </IconButton>
      </Tooltip>

      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 420,
            maxWidth: '90vw',
            borderRadius: 2,
            boxShadow: theme.shadows[8],
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }
        }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            Tenant Complaints
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {unreadCount} unread message{unreadCount !== 1 ? 's' : ''}
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ maxHeight: 450, overflowY: 'auto', p: 1 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
              <CircularProgress size={32} />
            </Box>
          ) : threads.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight={120}
              p={2}
            >
              <ChatBubble sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                All caught up!
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                No unread tenant messages at this time.
              </Typography>
            </Box>
          ) : (
            <List disablePadding sx={{ p: 1 }}>
              {threads.map((thread) => (
                <ComplaintItem
                  key={thread.id}
                  onClick={() => handleThreadClick(thread.id)}
                  sx={{
                    borderLeftWidth: 4,
                    borderLeftStyle: 'solid',
                    borderLeftColor: complaintsService.getStatusColor(thread.status) + '.main'
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: alpha('#4CAF50', 0.2),
                      color: '#4CAF50',
                      width: 40,
                      height: 40,
                      mr: 2
                    }}
                  >
                    <Person />
                  </Avatar>

                  <Box flex={1} minWidth={0}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{ flexGrow: 1 }}
                        noWrap
                      >
                        {thread.tenantName}
                      </Typography>
                      <Chip
                        label={`${thread.unreadByAdminCount} new`}
                        size="small"
                        color="error"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>

                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{
                        mb: 0.5,
                        lineHeight: 1.4,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {thread.title}
                    </Typography>

                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={complaintsService.getStatusLabel(thread.status)}
                        size="small"
                        color={complaintsService.getStatusColor(thread.status)}
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                      <Typography variant="caption" color="text.disabled" fontWeight={500}>
                        {formatTimeAgo(thread.lastActivityAt)}
                      </Typography>
                    </Box>
                  </Box>
                </ComplaintItem>
              ))}
            </List>
          )}
        </Box>

        {threads.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 1.5 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleViewAll}
                sx={{
                  borderColor: alpha('#4CAF50', 0.3),
                  color: '#4CAF50',
                  '&:hover': {
                    borderColor: '#4CAF50',
                    backgroundColor: alpha('#4CAF50', 0.08)
                  }
                }}
              >
                View All Complaints
              </Button>
            </Box>
          </>
        )}
      </Popover>
    </>
  );
}
